import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { imageApi } from '../../../api/imageApi.js';
import { tagApi } from '../../../api/tagApi.js';
import { NavBar } from '../../app/navBar.js';
import './searchDateScreen.css'; // You may need to create or adjust this CSS file

const SearchDateScreen = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [startDate, setStartDate] = useState(() => searchParams.get('startDate') || '');
    const [endDate, setEndDate] = useState(() => searchParams.get('endDate') || '');
    const [taggablePeople, setTaggablePeople] = useState([]);
    const [selectedPeopleIDs, setSelectedPeopleIDs] = useState(
        () => searchParams.get('people') ? searchParams.get('people').split(',') : []
    );
    const [peopleSearchText, setPeopleSearchText] = useState('');
    const [imageList, setImageList] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const response = await tagApi.getTaggablePeople();
                if (response.status === false && response.msg === "Forbidden") {
                    navigate('/login');
                    return;
                }

                if (response?.data?.data) {
                    setTaggablePeople(response.data.data);
                }
            } catch (error) {
                if (error.status === 403) {
                    navigate('/login');
                } else {
                    console.error('Error fetching taggable people:', error);
                }
            }
        })();
    }, [navigate]);

    // Auto-run search on mount if URL params are present
    useEffect(() => {
        const initStart = searchParams.get('startDate') || '';
        const initEnd = searchParams.get('endDate') || '';
        const initPeople = searchParams.get('people') ? searchParams.get('people').split(',') : [];

        if (initStart || initEnd || initPeople.length > 0) {
            runSearch(initStart, initEnd, initPeople);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const runSearch = async (start, end, people) => {
        setImageList([]);
        try {
            const response = await imageApi.searchByDate(start, end, people);
            if (response.status === false && response.msg === "Forbidden") {
                navigate('/login');
                return;
            }
            if (response && response.data && response.data.ids) {
                setImageList(response.data.ids.map(img => ({...img, src: null})));
            }
        } catch (error) {
            if (error.status === 403) {
                navigate('/login');
            } else {
                console.error('Error during search:', error);
            }
        }
    };

    const handleSearch = async (event) => {
        event.preventDefault();

        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (selectedPeopleIDs.length > 0) params.people = selectedPeopleIDs.join(',');
        setSearchParams(params, { replace: true });

        await runSearch(startDate, endDate, selectedPeopleIDs);
    };

    const handlePeopleToggle = (peopleID) => {
        setSelectedPeopleIDs((prev) => {
            if (prev.includes(peopleID)) {
                return prev.filter((id) => id !== peopleID);
            }
            return [...prev, peopleID];
        });
    };

    const clearPeopleSelection = () => {
        setSelectedPeopleIDs([]);
        setPeopleSearchText('');
    };

    // Method to load an image when it comes into the viewport
    const loadImage = async (img) => {
        const blob = await imageApi.getImageBytes(img.id);
        setImageList(prevImages => prevImages.map(image =>
            image.id === img.id ? {...img, src: blob} : image
        ));
    }

    // Intersection observer logic
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const imageObj = imageList.find(function (item) { return item.id === Number(entry.target.dataset.imgId) });
                    if (imageObj) {
                        loadImage(imageObj);
                    }
                }
            });
        });

        const images = document.querySelectorAll('.grid-images img');
        images.forEach(img => {
            if (!img.src || img.src === 'path_to_placeholder.png') {
                observer.observe(img);
            }
        });

        return () => observer.disconnect(); // Cleanup on component unmount
    }, [imageList]);

    const handleImageClick = (imageId) => {
        navigate(`/details/${imageId}`);
    };

    const normalizedPeopleSearchText = peopleSearchText.trim().toLowerCase();
    const filteredPeople = taggablePeople.filter((person) => {
        if (normalizedPeopleSearchText === '') {
            return true;
        }
        return person.name.toLowerCase().includes(normalizedPeopleSearchText);
    });

    return (
        <div>
            <NavBar />
            <form onSubmit={handleSearch} className="search-form">
                <label>
                    Start Date:
                    <input 
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </label>
                <label>
                    End Date:
                    <input 
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </label>
                <label>
                    Tagged People:
                    <input
                        type="text"
                        value={peopleSearchText}
                        onChange={(e) => setPeopleSearchText(e.target.value)}
                        placeholder="Filter people by name"
                    />
                </label>
                <div className="selected-people-summary">
                    {selectedPeopleIDs.length} selected
                    <button type="button" onClick={clearPeopleSelection}>Clear</button>
                </div>
                <div className="people-checkbox-list">
                    {filteredPeople.map((person) => {
                        const personID = String(person.id);
                        return (
                            <label key={person.id} className="people-checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={selectedPeopleIDs.includes(personID)}
                                    onChange={() => handlePeopleToggle(personID)}
                                />
                                <span>{person.name}</span>
                            </label>
                        );
                    })}
                </div>
                <button type="submit">Search</button>
            </form>

            <div className="grid-images">
                {imageList.map(image => (
                    <img
                        key={image.id}
                        data-img-id={image.id}
                        src={image.src}
                        alt={image.title}
                        onClick={() => handleImageClick(image.id)}
                    />
                ))}
            </div>
            {/* Add any additional logic for handling selectedImage as in homeScreen.js */}
        </div>
    );
}

export { SearchDateScreen };